/**
 * 镜头语言受控枚举(console 侧词表)—— 与 ai `prompt_lexicon_zh.py` + `camera_lexicon.py` **逐字对齐**。
 *
 * ═══ 为什么值必须逐字同源 ═══════════════════════════════════════════════════════
 * 编译层 `phrase_camera` 对枚举查表出中文短语,但 `CAMERA_MOVE_ZH.get(m, m)` 对未知值
 * **原样透传进 prompt** —— UI 若允许自由输入或值拼错,错的字会一路进模型。所以本表
 * 只做「选择器数据源」,分镜页**只能选不能写**;ai 侧曾因「枚举收窄但 prompt 教 18 值」
 * 静默丢过整个运镜子句(见 prompt_lexicon_zh.py 的唯一真相源裁决),console 这份照抄那份。
 *
 * ★运镜三档 = ai `camera_lexicon.py` 的动作预算档(safe/medium/high)。high 档(环绕/甩镜)
 * 只建议给 hook/高光镜 —— 这是**导演期的预算门**,渲染期编译层照单全收;UI 照实提示不硬拦
 * (实施拍板 2026-07-26:提示而非拦截,拦了就没有「用户明知超预算也要」的出口)。
 */

export interface LexOption {
  value: string;
  label: string;
}

/** 景别 6 值(ai FRAMING_ZH)。 */
export const SHOT_SIZES_LEX: readonly LexOption[] = [
  { value: "extreme_close_up", label: "大特写" },
  { value: "close_up", label: "特写" },
  { value: "medium_close", label: "中近景" },
  { value: "medium", label: "中景" },
  { value: "medium_wide", label: "中远景" },
  { value: "wide", label: "远景" },
];

export type CameraTier = "safe" | "medium" | "high";

/** 运镜 18 值三档(ai CAMERA_MOVE_ZH × camera_lexicon 三档)。 */
export const CAMERA_MOVES_LEX: readonly (LexOption & { tier: CameraTier })[] = [
  // safe
  { value: "static", label: "固定机位", tier: "safe" },
  { value: "dolly_in", label: "推镜", tier: "safe" },
  { value: "dolly_out", label: "拉镜", tier: "safe" },
  { value: "pan_left", label: "左摇", tier: "safe" },
  { value: "pan_right", label: "右摇", tier: "safe" },
  { value: "tilt_up", label: "上摇", tier: "safe" },
  { value: "tilt_down", label: "下摇", tier: "safe" },
  { value: "zoom_in", label: "变焦推近", tier: "safe" },
  { value: "zoom_out", label: "变焦拉远", tier: "safe" },
  { value: "rack_focus", label: "焦点转移", tier: "safe" },
  // medium
  { value: "tracking_left", label: "左横移跟拍", tier: "medium" },
  { value: "tracking_right", label: "右横移跟拍", tier: "medium" },
  { value: "steadicam", label: "稳定器跟随", tier: "medium" },
  { value: "crane_up", label: "升镜", tier: "medium" },
  { value: "crane_down", label: "降镜", tier: "medium" },
  { value: "handheld", label: "手持", tier: "medium" },
  // high(只建议 hook/高光镜)
  { value: "orbital", label: "环绕", tier: "high" },
  { value: "whip_pan", label: "甩镜", tier: "high" },
];

/** 镜速 3 值(ai CAMERA_SPEED_ZH;static 不接速度,选择器联动禁用)。 */
export const CAMERA_SPEEDS_LEX: readonly LexOption[] = [
  { value: "slow", label: "缓" },
  { value: "brisk", label: "利落" },
  { value: "punchy", label: "快" },
];

/** 布光 6 值(ai LIGHTING_ZH)。 */
export const LIGHTING_LEX: readonly LexOption[] = [
  { value: "high_key_warm", label: "暖调高调光" },
  { value: "low_key", label: "低调戏剧光" },
  { value: "rim", label: "轮廓光分离" },
  { value: "golden_hour", label: "黄金时刻暖光" },
  { value: "softbox_clean", label: "干净柔光" },
  { value: "neon", label: "霓虹光" },
];

/** 承接 6 值(ai CONTINUITY_ZH;首镜是开场,无承接)。 */
export const CONTINUITY_LEX: readonly LexOption[] = [
  { value: "CONTINUE_ACTION", label: "动作接续" },
  { value: "CONTINUE_SCENE", label: "同场景切换" },
  { value: "MATCH_ACTION", label: "匹配剪辑" },
  { value: "CUTAWAY", label: "插入切出" },
  { value: "SCENE_RESET", label: "硬切换场" },
  { value: "GRAPHIC_INSERT", label: "图形插入" },
];

const labelMap = (opts: readonly LexOption[]) =>
  Object.fromEntries(opts.map((o) => [o.value, o.label])) as Record<string, string>;

export const SHOT_SIZE_LABEL = labelMap(SHOT_SIZES_LEX);
export const CAMERA_MOVE_LABEL = labelMap(CAMERA_MOVES_LEX);
export const CAMERA_SPEED_LABEL = labelMap(CAMERA_SPEEDS_LEX);
export const LIGHTING_LABEL = labelMap(LIGHTING_LEX);
export const CONTINUITY_LABEL = labelMap(CONTINUITY_LEX);

/** 口播密度硬顶(字/秒)—— 与 ai 导演 prompt 的「秒数 × 5.5」同口径(超线 TTS 加速念糊,生产实锤 8.7 字/秒废片)。 */
export const VOICEOVER_DENSITY_MAX = 5.5;

/** 本镜口播密度(字/秒);duration ≤0 时按 0 处理(避免除零 NaN)。 */
export function voiceoverDensity(voiceover: string, durationSec: number): number {
  const chars = (voiceover ?? "").trim().length;
  if (!chars || durationSec <= 0) return 0;
  return Math.round((chars / durationSec) * 10) / 10;
}
